"use client";

/**
 * Allows user to switch to another space or view account settings
 */

import React, { Fragment, useState } from "react";
import styled from "styled-components";
import { buildURL, buildPath, PopupMenu, Box, utils, Tooltip } from "@thenounproject/lingo-core";
import useSpaces from "@redux/actions/spaces/useSpaces";
import useCurrentUser from "@actions/users/useCurrentUser";
import useNavPoint from "@hooks/useNavPoint";

const AvatarButton = styled.div`
  height: 32px;
  width: 100%;
  img {
    width: 32px;
    height: 32px;
    cursor: pointer;
    border-radius: 50%;
  }
`;

const AvatarButtonFlex = styled(AvatarButton)<{ isActive: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  cursor: pointer;
  background: ${props => (props.isActive ? utils.getColor("grayDarkest") : "transparent")};
  &:hover {
    background-color: ${utils.getColor("navbarHoverBackground")};
  }
`;

const SpacePicker: React.FC = () => {
  const [showMenu, setShowMenu] = useState(false);
  const MENU_ID = "space-picker";
  const { user } = useCurrentUser();
  const { data: spaces = [] } = useSpaces();
  const { space: currentSpace } = useNavPoint() ?? {};

  if (!user?.id) return null;

  function renderAvatar() {
    const avatar = currentSpace?.avatar ? (
      <img src={currentSpace.avatar} alt="Space avatar" />
    ) : (
      <img src={user.avatar} alt="User avatar" />
    );

    return (
      <Box onClick={() => setShowMenu(true)} data-popup-source={MENU_ID} pt="s">
        <AvatarButtonFlex data-tooltip-source={currentSpace?.avatar} isActive={showMenu}>
          {avatar}
        </AvatarButtonFlex>
        <Tooltip
          source={currentSpace?.avatar}
          direction={Tooltip.Direction.Right}
          ml="12"
          transitionDelay="0.5s"
          interactive={false}
          zIndex={5000}>
          Account
        </Tooltip>
      </Box>
    );
  }

  function renderMenu() {
    if (!showMenu) return null;

    const menuItems = spaces
      .filter(space => space.access.isPublic === false)
      .map(space => {
        return (
          <PopupMenu.Item
            key={space.id}
            title={space.name}
            link={buildURL("/dashboard", { space })}
            image={{ src: space.avatar, imageStyle: "circle" }}
          />
        );
      });

    menuItems.push(
      <PopupMenu.Item
        key="create-space-menu-item"
        title="Create a new space"
        image={{ iconId: "plus" }}
        link="/space/new/"
      />
    );

    return (
      <PopupMenu
        vPos="floatAbove"
        hPos="alignLeft"
        source={MENU_ID}
        close={() => setShowMenu(false)}>
        <PopupMenu.Section title="Spaces">{menuItems}</PopupMenu.Section>
        <PopupMenu.Section title="Other">
          <PopupMenu.Item
            image={{ iconId: "profile" }}
            title="Account settings"
            link={buildPath("/settings/profile/")}
          />
          <PopupMenu.Item image={{ iconId: "logout" }} title="Log out" link="/logout" />
        </PopupMenu.Section>
      </PopupMenu>
    );
  }

  return (
    <Fragment>
      {renderMenu()}
      {renderAvatar()}
    </Fragment>
  );
};
export default SpacePicker;
